import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {useCallback} from 'react'
import {graphql, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'

import type {ReactionViewerRelayLazyQuery} from './__generated__/ReactionViewerRelayLazyQuery.graphql'
import {VALUES} from './constants/values'
import {addReactionMutation} from './mutations/add-reaction-mutation'
import {removeReactionMutation} from './mutations/remove-reaction-mutation'
import {ReactionViewerBase} from './ReactionViewerBase'
import type {Reaction, ReactionContent, ReactionViewerGroup, Reactor} from './utils/ReactionGroups'

type ReactableIssue = NonNullable<NonNullable<IssueViewerViewQueryQuery['repository']>['issue']>
type ReactionGroupNode = NonNullable<NonNullable<ReactableIssue['reactionGroups']>[number]>

// `Omit` collapses a union to its common keys, so distribute it to keep each reactor member intact.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never

// The documents feeding this component select only `__typename`, `login` and the Bot extras, while
// `IssueViewerViewQuery` also selects `id` (and codegen adds `__isNode`). Drop those so the exported
// prop type promises no more than callers can supply.
type ReactorNode = DistributiveOmit<
  NonNullable<NonNullable<ReactionGroupNode['reactors']['nodes']>[number]>,
  'id' | '__isNode'
>

// Relay generates enums and abstract-type discriminators as "open": its reader types carry extra
// members for values the client doesn't know about, which the server-persisted-queries types omit.
// Accept those here so Relay data is assignable directly and callers need no cast; the mapper below
// narrows them back at runtime. `(string & {})` keeps the known members visible to autocomplete while
// still admitting a value added to the enum server-side.
type ReactionGroupData = Omit<ReactionGroupNode, 'content' | 'reactors'> & {
  readonly content: ReactionGroupNode['content'] | (string & {})
  readonly reactors: Omit<ReactionGroupNode['reactors'], 'nodes'> & {
    readonly nodes: ReadonlyArray<ReactorNode | {readonly __typename: '%other'} | null> | null
  }
}

// RELAY-CLEANUP(IssueBodyViewerReactable, ReactionViewerRelayLazyQuery):
// widens nullable fields so data read by these still-Relay documents stays assignable.
// Remove once all two have left Relay.
export type ReactionViewerRelayGroupsFragment = RelayCompat<{
  // Optional because callers may select this inside an `... on Reactable` inline fragment, where
  // Relay makes the field optional for nodes that aren't Reactable.
  readonly reactionGroups?: ReadonlyArray<ReactionGroupData | null> | null
}>

export type ReactionViewerRelayProps = {
  reactionGroups: ReactionViewerRelayGroupsFragment | null
  subjectId: string
  canReact?: boolean
}

export function ReactionViewerRelay({
  reactionGroups: reactionGroupsData,
  subjectId,
  canReact = true,
}: ReactionViewerRelayProps) {
  const environment = useRelayEnvironment()

  const onReact = useCallback(
    (reaction: ReactionContent, viewerHasReacted: boolean) => {
      if (!canReact) return
      if (viewerHasReacted) {
        removeReactionMutation({
          environment,
          input: {subject: subjectId, content: reaction},
        })
      } else {
        addReactionMutation({
          environment,
          input: {subject: subjectId, content: reaction},
        })
      }
    },
    [canReact, environment, subjectId],
  )

  // filtering out null reaction groups which can be expected when there's an error on the server
  const reactionViewerGroups = (reactionGroupsData?.reactionGroups || [])
    .filter(reactionGroup => reactionGroup != null)
    .map((reactionGroup): ReactionViewerGroup => {
      const reaction: Reaction = {
        // Narrow away Relay's open-enum member; unknown reaction types are not renderable.
        content: reactionGroup.content as ReactionContent,
        viewerHasReacted: reactionGroup.viewerHasReacted,
      }

      const reactors: Reactor[] =
        reactionGroup.reactors?.nodes?.map(reactor => {
          if (!reactor || reactor.__typename === '%other') {
            return {login: '', typeName: 'Other'}
          }
          let displayName = reactor.login
          if (reactor.__typename === 'Bot') {
            if (reactor.isCopilot) {
              displayName = VALUES.copilotDisplayName
            } else if (reactor.isAgent && reactor.displayName) {
              displayName = reactor.displayName
            }
          }
          return {
            login: displayName,
            typeName: reactor.__typename,
          }
        }) || []

      return {
        reaction,
        reactors,
        totalCount: reactionGroup.reactors?.totalCount || 0,
      }
    })

  return <ReactionViewerBase reactionGroups={reactionViewerGroups} canReact={canReact} onReact={onReact} />
}

/**
 * Query component for the ReactionViewer, used to fetch the reaction groups for a given subject.
 * @param id The relay ID of the subject to fetch reactions for.
 * @param subjectLocked Whether the subject is locked and reactions should not be allowed.
 * @returns The ReactionViewer component with the fetched reaction groups.
 */
export function ReactionViewerRelayQueryComponent({id, subjectLocked}: {id: string; subjectLocked?: boolean}) {
  const data = useLazyLoadQuery<ReactionViewerRelayLazyQuery>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      query ReactionViewerRelayLazyQuery($id: ID!) {
        node(id: $id) {
          ... on Reactable {
            reactionGroups {
              content
              viewerHasReacted
              reactors(first: 5) {
                totalCount
                nodes {
                  __typename
                  ... on User {
                    login
                  }
                  ... on Bot {
                    login
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on Organization {
                    login
                  }
                  ... on Mannequin {
                    login
                  }
                }
              }
            }
          }
        }
      }
    `,
    {id},
    {fetchPolicy: 'store-or-network'},
  )

  if (!data.node) {
    return null
  }

  return <ReactionViewerRelay subjectId={id} reactionGroups={data.node} canReact={!subjectLocked} />
}

export default ReactionViewerRelay
